import React from 'react';
import {
  GripVertical,
  Eye,
  Info,
  Table,
  Download,
  Locate,
  Palette,
  Filter,
  Trash2,
  SlidersHorizontal,
} from 'lucide-react';

export const MAP_LAYER_TOUR_STORAGE_KEY = 'manteo_map_layer_tour_completed';

const tourTarget = (layerId, control) => `[data-layer-tour="${layerId}-${control}"]`;

function TourIcon({ icon: Icon }) {
  return (
    <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0 border border-gray-200/50 dark:border-gray-700/50">
      <Icon size={13} className="text-gray-600 dark:text-gray-300" />
    </div>
  );
}

function TourContentRow({ icon, children }) {
  const Icon = icon;
  return (
    <div className="flex items-start gap-2.5">
      <TourIcon icon={Icon} />
      <p className="text-xs text-gray-600 dark:text-gray-400 leading-snug min-w-0">{children}</p>
    </div>
  );
}

function ActionTourRows({ rows }) {
  return (
    <div className="space-y-2">
      {rows.map(({ icon, label, description }) => (
        <TourContentRow key={label} icon={icon}>
          <span className="font-bold text-gray-900 dark:text-white">{label}</span>
          {description ? ` — ${description}` : ''}
        </TourContentRow>
      ))}
    </div>
  );
}

export function layerHasColorbar(layer) {
  return (
    layer.status !== 'loading'
    && layer.status !== 'error'
    && layer.data?.features?.length > 0
    && !layer.config?.highCardinalityCategorical
    && layer.stats
    && (layer.stats.is_categorical || (layer.stats.min !== undefined && layer.stats.max !== undefined))
  );
}

function buildActionTourRows(layer) {
  const hasError = layer.status === 'error';
  const hasData = layer.data?.features?.length > 0;
  const rows = [];

  if (!hasError && hasData) {
    rows.push(
      {
        icon: Table,
        label: 'Show Data Table',
        description: 'Open an inline attribute table for layer features',
      },
      {
        icon: Download,
        label: 'Download',
        description: 'Export this layer as GeoJSON',
      },
      {
        icon: Locate,
        label: 'Fly to Layer',
        description: 'Pan and zoom the map to this layer\'s extent',
      }
    );
  }

  if (!hasError) {
    rows.push({
      icon: Palette,
      label: 'Pick Color',
      description: 'Change palette, classification, and color property',
    });
  }

  rows.push({
    icon: Trash2,
    label: 'Delete',
    description: 'Remove from the map — the layer stays available in Workspace History',
  });

  return rows;
}

export function buildMapLayerTourSteps(layer, { isDraggable = false } = {}) {
  const id = layer.id;
  const isLoading = layer.status === 'loading';
  const hasDataset = Boolean(layer.dataset);

  if (isLoading) return [];

  const steps = [];

  if (isDraggable) {
    steps.push({
      target: tourTarget(id, 'drag'),
      title: 'Reorder Layers',
      content: (
        <TourContentRow icon={GripVertical}>
          Drag the handle to change <strong className="text-gray-900 dark:text-white font-semibold">stacking order</strong> on the map. Layers higher in the list draw on top.
        </TourContentRow>
      ),
      placement: 'right',
      icon: GripVertical,
    });
  }

  steps.push({
    target: tourTarget(id, 'visibility'),
    title: 'Show / Hide',
    content: (
      <TourContentRow icon={Eye}>
        Toggle layer <strong className="text-gray-900 dark:text-white font-semibold">visibility</strong> on the map without removing it.
      </TourContentRow>
    ),
    placement: 'right',
    icon: Eye,
  });

  if (hasDataset) {
    steps.push({
      target: tourTarget(id, 'info'),
      title: 'Layer Details',
      content: (
        <TourContentRow icon={Info}>
          Hover to see the <strong className="text-gray-900 dark:text-white font-semibold">dataset source</strong>, spatial column, filters, and other query details.
        </TourContentRow>
      ),
      placement: 'right',
      icon: Info,
    });
  }

  const actionRows = buildActionTourRows(layer);
  if (actionRows.length > 0) {
    steps.push({
      target: tourTarget(id, 'actions'),
      title: 'Layer Actions',
      content: <ActionTourRows rows={actionRows} />,
      placement: 'right',
      icon: SlidersHorizontal,
    });
  }

  if (layerHasColorbar(layer)) {
    steps.push({
      target: tourTarget(id, 'legend'),
      title: 'Legend Filter',
      content: (
        <TourContentRow icon={Filter}>
          Click a color band to <strong className="text-gray-900 dark:text-white font-semibold">filter the map</strong> to that value range. Use reset to clear the filter.
        </TourContentRow>
      ),
      placement: 'right',
      icon: Filter,
    });
  }

  return steps;
}
